import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkle, Spinner, CheckCircle, ArrowLeft, ArrowClockwise, CaretDown, CaretRight,
  ArrowUUpLeft, FileText,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MixedDocNotice from './MixedDocNotice';
import { dmsApi } from './dmsApi';
import { takeFiles } from './ingestQueue';
import { BAND_VIEW, STAGE_VIEW, TrafficDot, bandOf, fileIcon, hasThumb } from './docsUi';
import { DrawingReviewPanel, RetryModeDialog } from './DrawingReviewPanel';
import { DocFileViewer } from './DocFileViewer';
import { folderOptions } from './folderTree';

const StageChip = ({ item }) => {
  const st = STAGE_VIEW[item.stage] || STAGE_VIEW.uploading;
  const band = bandOf(item);
  // 順番待ちは「何件待ち」まで出す（止まって見えるのを防ぐ）。0 = 自分の順番。
  const queueLabel = item.stage === 'queued'
    ? (item.queue_position === 0 ? '読み取り中'
      : item.queue_position ? `順番待ち — ${item.queue_position}件目` : '順番待ち')
    : null;
  const label = item.stage === 'review' ? BAND_VIEW[band].label
    : item.stage === 'ready' && item.auto_applied ? '自動保存しました' : (queueLabel || st.label);
  const cls = item.stage === 'review' ? BAND_VIEW[band].box + ' ' + BAND_VIEW[band].text : st.cls;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium ${cls}`}
      data-testid={`dms-ingest-stage-${item.id}`}>
      {st.busy && <Spinner size={11} className="animate-spin" />}
      {item.stage === 'review' && <TrafficDot band={band} />}
      {item.stage === 'ready' && <CheckCircle size={11} weight="fill" />}
      {label}
    </span>
  );
};

const canPreviewByName = (name = '') => /\.(pdf|png|jpe?g|webp|gif|tiff?|bmp)$/i.test(name);

const Preview = ({ item }) => {
  const [url, setUrl] = useState(null);
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    let u = null; let alive = true;
    setUrl(null);
    if (item?.document_id) {
      dmsApi.thumbUrl(item.document_id).then((x) => {
        if (alive) { u = x; setUrl(x); } else { URL.revokeObjectURL(x); }
      }).catch(() => {});
    }
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [item?.document_id]);
  const kws = (item?.suggestion?.keywords || []).slice(0, 12);
  // 拡大できるかどうかは拡張子で判断する。取り込み中の行は mime_type を持たないため、
  // hasThumb() だけに頼るとPDFでも「プレビューできません」と出てしまう。
  const previewable = hasThumb(item || {}) || canPreviewByName(item?.filename);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="dms-ingest-preview">
      <button type="button" onClick={() => setZoom(true)} disabled={!item?.document_id}
        className="block w-full h-[440px] bg-zinc-50 overflow-auto cursor-zoom-in disabled:cursor-default"
        title="クリックで拡大" data-testid="dms-ingest-preview-zoom">
        <span className="flex h-full items-start justify-center">
          {url ? <img src={url} alt="" className="w-full object-contain object-top" />
            : <span className="flex flex-col items-center gap-2 self-center text-sm text-zinc-400">
              {fileIcon(item || {}, 34)}
              {previewable ? 'プレビューを準備しています…' : 'この形式はプレビューできません'}
              <span className="text-xs text-teal-700">クリックで拡大して確認できます</span>
            </span>}
        </span>
      </button>
      {url && (
        <p className="border-t border-zinc-100 px-3 py-1 text-center text-[11px] text-zinc-400">
          画像をクリックすると、拡大・移動できるビューアーで原寸まで確認できます
        </p>
      )}
      {zoom && (
        <DocFileViewer documentId={item.document_id} filename={item.filename}
          title={item?.suggestion?.title || item?.filename} thumbUrl={url}
          onClose={() => setZoom(false)} />
      )}
      {kws.length > 0 && (
        <div className="border-t border-zinc-200 p-3">
          <p className="text-xs font-semibold text-zinc-600 mb-1.5">この書類から読み取った語</p>
          <p className="flex flex-wrap gap-1.5">
            {kws.map((k) => (
              <span key={k} className="rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-600">{k}</span>
            ))}
          </p>
        </div>
      )}
    </div>
  );
};

const ReviewForm = ({ item, meta, folders, defaultFolderId, onSaved }) => {
  const s = useMemo(() => item?.suggestion || {}, [item]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);

  const band = bandOf(item);

  // 行が切り替わったときだけ初期化する（2.5秒ごとの更新で入力を消さない）
  useEffect(() => {
    const sug = item?.suggestion || {};
    setForm({
      title: sug.title || item?.filename || '',
      doc_type: sug.doc_type || 'other',
      description: sug.description || '',
      tags: (sug.tags || []).join(', '),
      folder_id: sug.folder_id || defaultFolderId || '',
      links: (sug.links || []).map((l) => l.key),
    });
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return null;
  const toggleLink = (key) => setForm((f) => ({
    ...f, links: f.links.includes(key) ? f.links.filter((k) => k !== key) : [...f.links, key],
  }));

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || '操作に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-3.5" data-testid="dms-ingest-form">
      <div className={`flex items-center gap-2 rounded-md border px-2.5 py-2 ${BAND_VIEW[band].box}`}>
        <TrafficDot band={band} />
        <span className={`text-sm font-medium ${BAND_VIEW[band].text}`}>{BAND_VIEW[band].label}</span>
      </div>
      {item.auto_retry?.attempted && (
        <div className={`rounded-md border px-2.5 py-2 text-[11px] ${
          item.auto_retry.running ? 'border-amber-200 bg-amber-50 text-amber-800'
            : item.auto_retry.recovered ? 'border-teal-200 bg-teal-50 text-teal-800'
              : 'border-zinc-200 bg-zinc-50 text-zinc-600'}`}
          data-testid={`dms-ingest-auto-retry-${item.id}`}>
          {item.auto_retry.running
            ? `1回目が失敗したため「${item.auto_retry.to_mode_label}」で自動的に読み直しています…`
            : item.auto_retry.recovered
              ? `1回目（${item.auto_retry.from_mode_label}）が失敗したため「${item.auto_retry.to_mode_label}」で自動的に読み直しました`
              : `「${item.auto_retry.from_mode_label}」と「${item.auto_retry.to_mode_label}」の両方で読めませんでした — 内容をご確認ください`}
          {item.auto_retry.first_error && (
            <span className="text-zinc-500"> · 1回目の原因: {item.auto_retry.first_error}</span>
          )}
        </div>
      )}
      {item.error && (
        <div className="rounded-md border border-rose-100 bg-rose-50 px-2.5 py-2 space-y-1"
          data-testid={`dms-ingest-error-${item.id}`}>
          <p className="text-xs text-rose-700">{item.error}</p>
          {item.failure && (
            <div className="text-[11px] text-rose-600/90 space-y-0.5"
              data-testid={`dms-ingest-failure-${item.id}`}>
              <p>
                <span className="font-semibold">原因: {item.failure.error_type_label}</span>
                {item.failure.http_status ? `（HTTP ${item.failure.http_status}）` : ''}
                {item.failure.stage ? ` · ${item.failure.stage} の段階` : ''}
              </p>
              {(item.failure.model || item.failure.provider_label) && (
                <p>使ったAI: {item.failure.provider_label || '—'} / {item.failure.model || '—'}</p>
              )}
              {item.failure.detail && (
                <p className="font-mono break-all text-[10px] text-rose-500">{item.failure.detail}</p>
              )}
            </div>
          )}
        </div>
      )}
      <div>
        <p className="text-xs font-semibold text-zinc-600 mb-1">タイトル</p>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="h-9" data-testid={`dms-ingest-title-${item.id}`} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-semibold text-zinc-600 mb-1">書類の種類</p>
          <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
            <SelectTrigger className="h-9 text-sm" data-testid={`dms-ingest-type-${item.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(meta?.doc_types || []).map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-xs font-semibold text-zinc-600 mb-1">保存フォルダー</p>
          <Select value={form.folder_id} onValueChange={(v) => setForm({ ...form, folder_id: v })}>
            <SelectTrigger className="h-9 text-sm" data-testid={`dms-ingest-folder-${item.id}`}>
              <SelectValue placeholder="フォルダーを選択" />
            </SelectTrigger>
            <SelectContent>
              {folderOptions(folders || []).map((o) => (
                <SelectItem key={o.value} value={o.value} title={o.pathLabel}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-zinc-600 mb-1">AIが付けたタグ</p>
        <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
          className="h-9" data-testid={`dms-ingest-tags-${item.id}`} />
        <p className="text-xs text-zinc-400 mt-1">カンマ区切りで編集できます</p>
      </div>
      {item.document_id && <MixedDocNotice documentId={item.document_id} />}
      <DrawingReviewPanel documentId={item.document_id} extraction={item.extraction}
        readInfo={item.read_info} />
      {s.description && (
        <p className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-2"
          data-testid={`dms-ingest-desc-${item.id}`}>{s.description}</p>
      )}
      {(s.links || []).length > 0 && (
        <div>
          <p className="text-xs font-semibold text-zinc-600 mb-1.5">本文から見つかった関連レコード</p>
          <div className="flex flex-wrap gap-1.5">
            {s.links.map((l) => {
              const on = form.links.includes(l.key);
              return (
                <button key={l.key} type="button" onClick={() => toggleLink(l.key)}
                  data-testid={`dms-ingest-link-${item.id}-${l.type}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors duration-200 ${
                    on ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-zinc-200 bg-white text-zinc-500'}`}>
                  {on ? <CheckCircle size={12} weight="fill" /> : <span className="w-3" />}
                  <span className="text-zinc-400">{l.type_label}</span>{l.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap pt-1">
        <Button disabled={busy} className="h-9 bg-teal-700 hover:bg-teal-800 text-sm"
          data-testid={`dms-ingest-save-${item.id}`}
          onClick={() => run(() => dmsApi.ingestApply(item.id, {
            title: form.title, doc_type: form.doc_type, description: form.description,
            ai_tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
            folder_id: form.folder_id, link_keys: form.links,
          }), `「${form.title}」を保存しました`)}>
          {busy && <Spinner size={14} className="animate-spin mr-1.5" />}この内容で保存
        </Button>
        <Button variant="outline" disabled={busy} className="h-9 text-sm border-zinc-200"
          data-testid={`dms-ingest-retry-${item.id}`}
          onClick={() => setRetryOpen(true)}>
          <ArrowClockwise size={14} className="mr-1.5" />もう一度読み取る
        </Button>
        <RetryModeDialog open={retryOpen} busy={busy} onClose={() => setRetryOpen(false)}
          onPick={(mode) => {
            setRetryOpen(false);
            run(() => dmsApi.ingestRetry(item.id, mode),
              mode === 'engineering_drawing' ? '図面として読み取ります' : '通常の書類として読み取ります');
          }} />
        <Button variant="ghost" disabled={busy} className="h-9 text-sm text-zinc-500"
          data-testid={`dms-ingest-skip-${item.id}`}
          onClick={() => run(() => dmsApi.ingestSkip(item.id), 'AI提案を使わずに残しました')}>
          AI提案を使わずに保存
        </Button>
      </div>
      <p className="text-xs text-zinc-400">ファイルはこのフォルダーに保存済みです。ここでの操作はタイトル・種類・タグ・関連レコードの確定だけです。</p>
    </div>
  );
};

/** 取り込みワークスペース（全画面）— 例外だけを確認して終わる画面。 */
export default function IngestWorkspacePage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const wantedItem = params.get('item');
  const [data, setData] = useState(null);
  const [others, setOthers] = useState([]);
  const confirmed = useRef(false);
  const [meta, setMeta] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [uploading, setUploading] = useState(0);
  const [showSaved, setShowSaved] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const timer = useRef(null);
  const started = useRef(false);

  const load = useCallback(async () => {
    try { setData(await dmsApi.ingestBatch(batchId)); }
    catch (e) {
      if (e?.response?.status === 404) { toast.error('取り込みが見つかりません'); navigate('/documents'); }
    }
    try { setOthers(await dmsApi.ingestActive()); } catch { /* noop */ }
  }, [batchId, navigate]);

  useEffect(() => {
    dmsApi.meta().then(setMeta).catch(() => {});
    dmsApi.folders().then((f) => setFolders(f.filter((x) => !x.is_archive_folder))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // ルート遷移前に選んだファイルをこの画面でアップロードする（3並列・1件の失敗は他に影響しない）
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const files = takeFiles(batchId);
    if (!files.length) return;
    setUploading(files.length);
    let idx = 0;
    const worker = async () => {
      while (idx < files.length) {
        const f = files[idx];
        idx += 1;
        const fd = new FormData();
        fd.append('file', f);
        try { await dmsApi.ingestFile(batchId, fd); } catch { /* 行として記録される */ }
        setUploading((n) => Math.max(0, n - 1));
        load();
      }
    };
    Promise.all([worker(), worker(), worker()]);
  }, [batchId, load]);

  const c = data?.counts || {};
  const items = useMemo(() => data?.items || [], [data]);
  const total = Math.max(c.total || 0, data?.batch?.expected_count || 0);
  const reviewItems = useMemo(() => items
    .filter((i) => i.stage === 'review')
    .sort((a, b) => (bandOf(a) === 'required' ? -1 : 1) - (bandOf(b) === 'required' ? -1 : 1)), [items]);
  const busyItems = items.filter((i) => STAGE_VIEW[i.stage]?.busy);
  const savedItems = items.filter((i) => i.stage === 'ready');
  const failedItems = items.filter((i) => i.stage === 'failed');
  const dupItems = items.filter((i) => i.stage === 'duplicate');

  useEffect(() => {
    if (!reviewItems.length) { setSelectedId(null); return; }
    if (wantedItem && reviewItems.some((i) => i.id === wantedItem)
        && selectedId !== wantedItem && !confirmed.current) {
      setSelectedId(wantedItem);
      return;
    }
    if (!reviewItems.some((i) => i.id === selectedId)) setSelectedId(reviewItems[0].id);
  }, [reviewItems, selectedId, wantedItem]);

  useEffect(() => {
    if (!busyItems.length && !uploading && (c.active ?? 0) === 0) return undefined;
    timer.current = setTimeout(load, 2500);
    return () => clearTimeout(timer.current);
  }, [data, load, uploading, busyItems.length, c.active]);

  const selected = reviewItems.find((i) => i.id === selectedId) || null;
  const reading = (c.reading || 0) + uploading;
  const q = data?.queue || {};

  // 最後の1件を確認し終わったら、次に開いている取り込みへ進む（ここで止まらない）
  const nextBatch = (others || []).find(
    (b) => b.id !== batchId && ((b.counts || {}).needs_review || 0) > 0) || null;
  useEffect(() => {
    if (!confirmed.current || !nextBatch) return;
    if (reviewItems.length || reading > 0) return;
    confirmed.current = false;
    toast.success('次の取り込みの確認に進みます');
    navigate(`/documents/ingest/${nextBatch.id}`);
  }, [nextBatch, reviewItems.length, reading, navigate]);

  const headline = reading > 0
    ? `${total}件中 ${reading}件を読み取り中${c.needs_review ? ` — ${c.needs_review}件の確認をお願いします` : ''}`
    : c.needs_review > 0
      ? `${c.needs_review}件の確認をお願いします${c.auto_saved ? `（${c.auto_saved}件は自動で保存しました）` : ''}`
      : `${total}件の取り込みが完了しました`;

  const finish = async () => {
    try { await dmsApi.ingestClose(batchId); } catch { /* noop */ }
    navigate('/documents');
  };

  const undo = async () => {
    if (!window.confirm('自動で保存した内容（タイトル・種類・タグ・関連レコード）を元に戻します。ファイル自体は削除されません。')) return;
    setUndoing(true);
    try {
      const r = await dmsApi.ingestUndo(batchId);
      toast.success(`${r.reverted}件の自動保存を取り消しました`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '取り消しに失敗しました'); }
    finally { setUndoing(false); }
  };

  if (!data) return <p className="py-20 text-center text-sm text-zinc-400">読み込み中...</p>;

  return (
    <div className="max-w-[1600px] mx-auto" data-testid="dms-ingest-workspace">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <button type="button" onClick={() => navigate('/documents/ingest')}
            data-testid="dms-ingest-all-link"
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-teal-700 transition-colors mb-1">
            <ArrowLeft size={14} /> 取り込みの確認（全件）
          </button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-teal-700" /> ファイルの取り込み
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            保存先: {data.batch?.folder_name} — AIが読み取った内容を確認して確定します
          </p>
        </div>
        <Button onClick={finish} variant={reviewItems.length > 0 ? 'outline' : 'default'}
          className={reviewItems.length > 0
            ? 'h-10 text-sm border-zinc-200 text-zinc-700'
            : 'h-10 bg-teal-700 hover:bg-teal-800 text-sm'}
          data-testid="dms-ingest-finish">確認を終える</Button>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-4 mb-6" data-testid="dms-ingest-progress">
        <p className="text-base text-zinc-900 font-medium">{headline}</p>
        <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden mt-3">
          <div className="h-full bg-teal-700 transition-[width] duration-500 ease-out"
            style={{ width: `${total ? Math.round(((c.done || 0) / total) * 100) : 0}%` }} />
        </div>
        {(c.needs_review > 0 || failedItems.length > 0 || dupItems.length > 0) && (
          <p className="flex items-center gap-3 flex-wrap mt-2.5">
            {dupItems.length > 0 && (
              <span className="text-sm text-orange-700" data-testid="dms-ingest-stat-duplicate"
                title={dupItems.map((i) => `${i.filename}: ${i.error || ''}`).join('\n')}>
                既にある書類と同じため取り込みませんでした {dupItems.length}件
              </span>
            )}
            {failedItems.length > 0 && (
              <span className="text-sm text-rose-700" data-testid="dms-ingest-stat-failed">
                取り込めなかったファイル {failedItems.length}件
              </span>
            )}
            {(c.needs_review > 0 || failedItems.length > 0) && (
              <Button variant="outline" size="sm" disabled={retrying}
                className="h-8 text-sm border-zinc-200"
                data-testid="dms-ingest-retry-failed"
                onClick={async () => {
                  setRetrying(true);
                  try {
                    const r = await dmsApi.ingestRetryFailed(batchId);
                    toast.success(r.queued
                      ? `${r.queued}件をまとめて読み直しています`
                      : '読み直す対象がありません');
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || '再読み取りに失敗しました'); }
                  finally { setRetrying(false); }
                }}>
                {retrying ? <Spinner size={13} className="animate-spin mr-1.5" />
                  : <ArrowClockwise size={13} className="mr-1.5" />}
                失敗した分をまとめて再読み取り
              </Button>
            )}
            {c.review_recommended > 0 && (
              <Button variant="outline" size="sm" className="h-8 text-sm border-zinc-200 ml-auto"
                data-testid="dms-ingest-apply-confident"
                onClick={async () => {
                  try {
                    const r = await dmsApi.ingestApplyConfident(batchId);
                    toast.success(r.applied ? `${r.applied}件を保存しました` : '確認が必要な書類のみ残っています');
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
                }}>
                おすすめの{c.review_recommended}件をまとめて保存
              </Button>
            )}
          </p>
        )}
      </div>

      {/* 読み取りキューの状況 — 「自分の前に何件／誰が並んでいるか」が見えないと
          待ちが止まっているように見える。同時実行は全社で max_concurrent 件、
          1人あたり1件なので、大量投入した人が他の人を塞がない。 */}
      {(q.queued > 0 || q.running > 0) && (
        <div className="mb-5 rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-2.5"
          data-testid="dms-ingest-queue-banner">
          <div className="flex items-center gap-3 flex-wrap text-sm">
            <span className="inline-flex items-center gap-1.5 font-semibold text-sky-900">
              <Spinner size={14} className="animate-spin" /> 読み取り待ち行列
            </span>
            <span className="text-sky-900" data-testid="dms-ingest-queue-running">処理中 {q.running}件</span>
            <span className="text-sky-900" data-testid="dms-ingest-queue-queued">待機 {q.queued}件</span>
            <span className="text-sky-800" data-testid="dms-ingest-queue-users">{q.users_waiting}人が利用中</span>
            {q.my_position ? (
              <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-sky-800 border border-sky-200"
                data-testid="dms-ingest-queue-mine">
                あなたの次の書類は {q.my_position}件目（自分の待機 {q.my_queued}件）
              </span>
            ) : null}
            <span className="ml-auto text-xs text-sky-700">
              同時に {q.max_concurrent}件まで・1人あたり1件ずつ
            </span>
          </div>
          {(q.users || []).length > 1 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="dms-ingest-queue-userlist">
              {q.users.map((u) => (
                <span key={u.user_id || u.user_name}
                  className="rounded-full bg-white border border-sky-200 px-2 py-0.5 text-[11px] text-sky-900">
                  {u.user_name}: {u.running ? '処理中' : `待機 ${u.queued}`}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {c.auto_saved > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 mb-6" data-testid="dms-ingest-autosaved">
          <div className="flex items-center gap-2 px-4 py-3">
            <CheckCircle size={16} weight="fill" className="text-emerald-600" />
            <button type="button" onClick={() => setShowSaved((v) => !v)}
              data-testid="dms-ingest-autosaved-toggle"
              className="text-sm font-medium text-emerald-900 flex items-center gap-1">
              自動で保存しました（{c.auto_saved}件）
              {showSaved ? <CaretDown size={12} /> : <CaretRight size={12} />}
            </button>
            <Button variant="ghost" size="sm" disabled={undoing} onClick={undo}
              data-testid="dms-ingest-undo"
              className="ml-auto h-8 text-sm text-emerald-900 hover:bg-emerald-100">
              <ArrowUUpLeft size={13} className="mr-1.5" />自動保存を取り消す
            </Button>
          </div>
          {showSaved && (
            <ul className="border-t border-emerald-200 px-4 py-2 space-y-1">
              {savedItems.filter((i) => i.auto_applied).map((i) => (
                <li key={i.id} className="text-sm text-emerald-900 flex items-center gap-2"
                  data-testid={`dms-ingest-saved-${i.id}`}>
                  <FileText size={13} className="text-emerald-600" />
                  {i.suggestion?.title || i.filename}
                  <span className="text-xs text-emerald-700">{i.suggestion?.doc_type_label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {reviewItems.length === 0 ? (
        <div className="rounded-lg border border-zinc-200 bg-white py-16 text-center" data-testid="dms-ingest-empty">
          {reading > 0 ? (
            <p className="text-sm text-zinc-500">
              <Spinner size={16} className="animate-spin inline mr-2" />読み取りが終わるまでお待ちください
            </p>
          ) : (
            <>
              <CheckCircle size={34} weight="duotone" className="text-emerald-500 mx-auto mb-2" />
              <p className="text-base text-zinc-800 font-medium">確認が必要な書類はありません</p>
              <p className="text-sm text-zinc-500 mt-1">すべて保存され、すぐに検索できます</p>
              {nextBatch && (
                <Button className="mt-4 h-9 bg-teal-700 hover:bg-teal-800 text-sm"
                  data-testid="dms-ingest-next-batch"
                  onClick={() => navigate(`/documents/ingest/${nextBatch.id}`)}>
                  次の取り込みを確認する（{(nextBatch.counts || {}).needs_review}件）
                </Button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="dms-ingest-review">
          <div className="lg:col-span-3 space-y-2">
            <p className="text-sm font-semibold text-zinc-700">確認する書類（{reviewItems.length}）</p>
            {reviewItems.map((i) => (
              <button key={i.id} type="button" onClick={() => setSelectedId(i.id)}
                data-testid={`dms-ingest-item-${i.id}`}
                className={`w-full text-left rounded-lg border px-3 py-2.5 transition-[border-color,background-color] duration-200 ${
                  i.id === selectedId ? 'border-teal-500 bg-teal-50/60' : 'border-zinc-200 bg-white hover:border-teal-300'}`}>
                <p className="text-sm font-medium text-zinc-900 truncate">{i.suggestion?.title || i.filename}</p>
                <p className="text-xs text-zinc-400 truncate">{i.filename}</p>
                <span className="mt-1.5 inline-flex"><StageChip item={i} /></span>
              </button>
            ))}
            {busyItems.length > 0 && (
              <p className="text-xs text-zinc-400 pt-1">
                <Spinner size={11} className="animate-spin inline mr-1" />
                {busyItems.length}件を読み取り中
              </p>
            )}
          </div>
          <div className="lg:col-span-5">{selected && <Preview item={selected} />}</div>
          <div className="lg:col-span-4">
            {selected && (
              <ReviewForm item={selected} meta={meta} folders={folders}
                defaultFolderId={data.batch?.folder_id}
                onSaved={() => { confirmed.current = true; setSelectedId(null); load(); }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
